// Add / edit income modal. Extracted from Income.jsx so it can be reused
// from the Dashboard's Step 3 empty-state CTA and any other page that needs
// to create/edit an income row without navigating away.
import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export function IncomeModal({ income, onClose, onSaved }) {
  const [form, setForm] = useState(income
    ? {
        source: income.source, amount: income.amount, expected_date: income.expected_date,
        received_date: income.received_date || "", frequency: income.frequency,
        status: income.status, notes: income.notes || "", end_date: income.end_date || "",
      }
    : { source: "", amount: "", expected_date: "", received_date: "",
        frequency: "biweekly", status: "expected", notes: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        source: form.source,
        amount: Number(form.amount),
        expected_date: form.expected_date,
        frequency: form.frequency,
        status: form.status,
        notes: form.notes || null,
        end_date: form.end_date || (income && income.end_date ? "" : null),
      };
      if (form.status === "received") payload.received_date = form.received_date || form.expected_date;
      else if (income && income.received_date) payload.received_date = "";
      const { data } = income
        ? await api.patch(`/incomes/${income.id}`, payload)
        : await api.post("/incomes", payload);
      toast.success(income ? "Income updated" : "Income added");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const isRecurring = form.frequency !== "unscheduled";

  return (
    <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form className="card-soft p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="income-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-medium">{income ? "Edit income" : "Add income"}</h2>
          <button type="button" className="btn-ghost !p-2" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Income Source</label>
            <input required className="input-soft" value={form.source} onChange={(e) => setForm({...form, source: e.target.value})} placeholder="e.g. Employer Payroll" data-testid="income-modal-source" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase text-[#59605C]">Amount</label>
              <input required type="number" step="0.01" className="input-soft" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} data-testid="income-modal-amount" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-[#59605C]">{isRecurring ? "Start date" : "Expected date"}</label>
              <input required type="date" className="input-soft" value={form.expected_date} onChange={(e) => setForm({...form, expected_date: e.target.value})} data-testid="income-modal-date" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-[#59605C]">Frequency</label>
              <select className="input-soft" value={form.frequency} onChange={(e) => setForm({...form, frequency: e.target.value})} data-testid="income-modal-frequency">
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="unscheduled">One-time</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-[#59605C]">Status</label>
              <select className="input-soft" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="income-modal-status">
                <option value="expected">Expected</option>
                <option value="received">Received</option>
              </select>
            </div>
            {isRecurring && (
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase text-[#59605C]">End date <span className="text-[#8A918D] normal-case italic">(optional — leave blank for ongoing)</span></label>
                <input type="date" className="input-soft" value={form.end_date}
                  onChange={(e) => setForm({...form, end_date: e.target.value})}
                  data-testid="income-modal-end-date" />
                <p className="text-[10px] text-[#8A918D] mt-1">
                  If set, no more occurrences of this schedule will appear in pay-period forecasts after this date.
                </p>
              </div>
            )}
            {form.status === "received" && (
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase text-[#59605C]">Received date</label>
                <input type="date" className="input-soft" value={form.received_date} onChange={(e) => setForm({...form, received_date: e.target.value})} data-testid="income-modal-received-date" />
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="income-modal-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
